import MainForm from "@components/common/MainForm";
import { Typography } from "@mui/material";
import { revalidateLogic } from "@tanstack/react-form";
import type { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useAppForm } from "@/components/Forms";

import { useConfirmPasswordChange } from "@/hooks/use-forgot-password";

import { NewPasswordFormSection } from "../../SetNewPassword/NewPasswordFormSection";
import { AuthenticateCode } from "../AuthenticateCode";

const ConfirmResetSchema = z
  .object({
    email: z.string().optional(),
    code: z.string().min(1, "field-required"),
    password: z.string().min(8, "password-at-least-8-characters"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "passwords-do-not-match",
    path: ["confirmPassword"],
  });

type ConfirmResetValues = z.infer<typeof ConfirmResetSchema>;

type Props = {
  email: string;
  onSuccess: () => void;
};

export const ConfirmResetForm: FunctionComponent<Props> = ({ email, onSuccess }) => {
  const { t } = useTranslation();
  const mutation = useConfirmPasswordChange();

  const form = useAppForm({
    defaultValues: {
      email,
      code: "",
      password: "",
      confirmPassword: "",
    } as ConfirmResetValues,
    validationLogic: revalidateLogic(),
    validators: {
      onChange: ConfirmResetSchema,
    },
    onSubmit: async ({ value }) => {
      await mutation.mutateAsync({ username: email, code: value.code, password: value.password });
      onSuccess();
    },
  });

  const errorMessage = (mutation.error as Error | undefined)?.message;

  return (
    <form.AppForm>
      <form.Subscribe
        selector={(s) => ({
          isValid: s.isValid,
        })}
      >
        {({ isValid }) => (
          <MainForm
            modalForm
            isValid={isValid}
            onSubmit={() => {
              form.handleSubmit();
            }}
            loading={mutation.isPending}
            title={t("common.form.input.set-new-password")}
          >
            {errorMessage && (
              <Typography
                sx={{
                  color: "error.main",
                  fontSize: "0.8rem",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                {errorMessage}
              </Typography>
            )}
            <AuthenticateCode form={form} identifier="code" />
            <NewPasswordFormSection form={form} email={email} />
          </MainForm>
        )}
      </form.Subscribe>
    </form.AppForm>
  );
};

export default ConfirmResetForm;
